import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const PhotoBoothContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  margin: 30px auto;
  max-width: 1000px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const PhotoTitle = styled.h2`
  font-size: 2.2em;
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  margin-bottom: 10px;
`;

const PhotoInstructions = styled.p`
  font-size: 1.1em;
  color: #ffe066;
  margin-bottom: 20px;
`;

const PhotoControls = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PhotoBtn = styled.button`
  padding: 12px 30px;
  font-size: 1.2em;
  background: linear-gradient(135deg, #4ecdc4, #44a08d);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s;
  font-family: 'Comic Sans MS', cursive, sans-serif;
  font-weight: bold;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 7px 20px rgba(0, 0, 0, 0.4);
  }
`;

const PhotoContainer = styled.div`
  margin: 20px 0;
`;

const VideoWrapper = styled.div`
  position: relative;
  display: inline-block;
  border: 5px solid white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const PhotoVideo = styled.video`
  display: block;
  max-width: 100%;
  height: auto;
  border-radius: 10px;
`;

const DraggableSteven = styled.img`
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  touch-action: none;
`;

const PhotoCanvas = styled.canvas`
  display: none;
`;

const PhotoGallery = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 15px;

  h3 {
    color: #ffe066;
    font-size: 1.8em;
    margin-bottom: 20px;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
`;

const GalleryItem = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const DownloadPhotoBtn = styled.button`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: 2px solid white;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9em;
  transition: all 0.3s;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: translateX(-50%) scale(1.05);
  }
`;

function PhotoBooth() {
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [stevenPos, setStevenPos] = useState({ x: 50, y: 50 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setCameraActive(true);
    } catch (error) {
      alert('Could not access camera: ' + error.message);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  };

  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame
    ctx.drawImage(video, 0, 0);

    // Draw Steven face overlay
    const steven = new Image();
    steven.src = Math.random() > 0.5 ? '/frolke2.jpg' : '/frolke.jpg';
    steven.onload = () => {
      ctx.drawImage(steven, stevenPos.x, stevenPos.y, 150, 150);
      
      // Save photo
      const photoUrl = canvas.toDataURL('image/png');
      setPhotos(prev => [...prev, photoUrl]);
    };
  };

  const downloadPhoto = (photoUrl, index) => {
    const link = document.createElement('a');
    link.href = photoUrl;
    link.download = `steven-photo-${index + 1}.png`;
    link.click();
  };

  const handleMouseDown = (e) => {
    const rect = videoRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if click is on Steven face
    if (
      x >= stevenPos.x && x <= stevenPos.x + 150 &&
      y >= stevenPos.y && y <= stevenPos.y + 150
    ) {
      setDragging(true);
      setDragOffset({ x: x - stevenPos.x, y: y - stevenPos.y });
    }
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    
    const rect = videoRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - dragOffset.x;
    const y = e.clientY - rect.top - dragOffset.y;
    
    setStevenPos({ x, y });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  const handleTouchStart = (e) => {
    const rect = videoRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    if (
      x >= stevenPos.x && x <= stevenPos.x + 150 &&
      y >= stevenPos.y && y <= stevenPos.y + 150
    ) {
      setDragging(true);
      setDragOffset({ x: x - stevenPos.x, y: y - stevenPos.y });
    }
  };

  const handleTouchMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    
    const rect = videoRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left - dragOffset.x;
    const y = touch.clientY - rect.top - dragOffset.y;
    
    setStevenPos({ x, y });
  };

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return (
    <PhotoBoothContainer>
      <PhotoTitle>📸 PHOTO BOOTH WITH STEVEN 📸</PhotoTitle>
      <PhotoInstructions>Drag Steven's face around and snap a pic!</PhotoInstructions>
      
      <PhotoControls>
        {!cameraActive ? (
          <PhotoBtn onClick={startCamera}>📷 START CAMERA</PhotoBtn>
        ) : (
          <>
            <PhotoBtn onClick={takePhoto}>📸 TAKE PHOTO</PhotoBtn>
            <PhotoBtn onClick={stopCamera}>🛑 STOP CAMERA</PhotoBtn>
          </>
        )}
      </PhotoControls>

      <PhotoContainer>
        <VideoWrapper
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleMouseUp}
        >
          <PhotoVideo
            ref={videoRef} 
            autoPlay 
            playsInline
          />
          {cameraActive && (
            <DraggableSteven
              src={Math.random() > 0.5 ? '/frolke2.jpg' : '/frolke.jpg'} 
              style={{
                left: `${stevenPos.x}px`,
                top: `${stevenPos.y}px`,
                cursor: dragging ? 'grabbing' : 'grab'
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              alt="Steven"
              draggable="false"
            />
          )}
        </VideoWrapper>
        <PhotoCanvas ref={canvasRef} />
      </PhotoContainer>

      {photos.length > 0 && (
        <PhotoGallery>
          <h3>Your Photos with Steven:</h3>
          <GalleryGrid>
            {photos.map((photo, index) => (
              <GalleryItem key={index}>
                <img src={photo} alt={`Photo ${index + 1}`} />
                <DownloadPhotoBtn
                  onClick={() => downloadPhoto(photo, index)}
                >
                  ⬇️ Download
                </DownloadPhotoBtn>
              </GalleryItem>
            ))}
          </GalleryGrid>
        </PhotoGallery>
      )}
    </PhotoBoothContainer>
  );
}

export default PhotoBooth;
